import React, { useMemo } from 'react';
import { Check, X } from 'lucide-react';
import { lineDiff, collapseContext } from './lineDiff';

const ROW_STYLE = {
  ctx: { background: 'transparent', color: 'var(--text-secondary)' },
  del: { background: 'var(--surface-red)', color: 'var(--text-critical)' },
  add: { background: 'var(--surface-green)', color: 'var(--text-green)' },
};
const MARKERS = { ctx: ' ', del: '-', add: '+' };

// "Fix with AI" diff preview (doc-87): the AI's corrected document rendered
// as a line diff against the current buffer. The buffer is NEVER touched
// here — Apply hands the fixed text up, Discard just closes the pane.
export default function AiFixPane({ currentText, fixedText, onApply, onDiscard }) {
  const rows = useMemo(
    () => collapseContext(lineDiff(currentText, fixedText)),
    [currentText, fixedText]
  );
  const added = rows.filter((r) => r.op === 'add').length;
  const removed = rows.filter((r) => r.op === 'del').length;
  const changed = added > 0 || removed > 0;

  return (
    <div className="pb-item stack-sm" role="region" aria-label="AI fix preview">
      <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>AI fix — review before applying</span>
        {changed ? (
          <span className="muted" style={{ fontSize: 12 }}>
            {added} line{added === 1 ? '' : 's'} added · {removed} removed. Nothing changes until you apply.
          </span>
        ) : (
          <span className="muted" style={{ fontSize: 12 }}>The AI returned the document unchanged.</span>
        )}
      </div>
      {changed && (
        <div
          className="mono"
          style={{
            border: '1px solid var(--border-secondary)',
            borderRadius: 6,
            maxHeight: 320,
            overflow: 'auto',
            fontSize: 12,
            lineHeight: 1.5,
            padding: '6px 0',
          }}
        >
          {rows.map((r, i) => (
            r.op === 'skip' ? (
              <div key={i} className="muted" style={{ padding: '0 10px', fontStyle: 'italic' }}>
                ⋯ {r.count} unchanged line{r.count === 1 ? '' : 's'}
              </div>
            ) : (
              <div key={i} style={{ ...ROW_STYLE[r.op], padding: '0 10px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {MARKERS[r.op]} {r.line}
              </div>
            )
          ))}
        </div>
      )}
      <div className="row" style={{ gap: 8 }}>
        {changed && (
          <button type="button" className="btn primary sm" onClick={onApply}>
            <Check className="w-3.5 h-3.5" />
            Apply fix
          </button>
        )}
        <button type="button" className="btn ghost sm" onClick={onDiscard}>
          <X className="w-3.5 h-3.5" />
          Discard
        </button>
      </div>
    </div>
  );
}
